import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { X, Gift, ArrowRight, Clock, Zap } from 'lucide-react'
import { sendExitIntentEmail } from '../utils/emailService'

const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasShown, setHasShown] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (hasShown) return

    const handleMouseLeave = (e) => {
      // Trigger only when mouse leaves from top of viewport
      if (e.clientY <= 0 && !hasShown) {
        setIsOpen(true)
        setHasShown(true)
      }
    }

    document.addEventListener('mouseleave', handleMouseLeave)
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [hasShown])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return

    setIsLoading(true)
    setError('')

    const result = await sendExitIntentEmail(email)

    if (result.success) {
      setIsSubmitted(true)
      setTimeout(() => {
        setIsOpen(false)
      }, 3000)
    } else {
      setError('Ndodhi një gabim. Ju lutem provoni përsëri.')
    }

    setIsLoading(false)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 sm:p-6"
        >
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="bg-gradient-to-br from-bronze-900 via-bronze-950 to-dark rounded-3xl overflow-hidden border-2 border-gold-400/30 shadow-2xl">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-bronze-800 hover:bg-bronze-700 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-cream" />
              </button>

              {!isSubmitted ? (
                <div className="p-8 md:p-12">
                  {/* Icon */}
                  <motion.div
                    animate={{ 
                      rotate: [0, -10, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 0.6 }}
                    className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center"
                  >
                    <Gift className="w-10 h-10 text-white" />
                  </motion.div>

                  {/* Title */}
                  <h2 className="text-3xl md:text-4xl font-display font-bold text-center text-cream mb-4">
                    Prit! 🎁 <span className="text-gradient-gold">Ofertë Ekskluzive</span>
                  </h2>

                  {/* Subtitle */}
                  <p className="text-beige-300 text-center text-lg mb-8">
                    Para se të largohesh, merr <span className="text-gold-400 font-bold">15% ZBRITJE</span> në konsultimin e parë + një raport FALAS SEO për website-in tënd!
                  </p>

                  {/* Benefits */}
                  <div className="grid md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-bronze-800/50 backdrop-blur-sm rounded-xl p-4 text-center">
                      <Zap className="w-8 h-8 mx-auto mb-2 text-gold-400" />
                      <div className="text-cream font-semibold mb-1">15% Zbritje</div>
                      <div className="text-beige-400 text-sm">Për projektin e parë</div>
                    </div>
                    <div className="bg-bronze-800/50 backdrop-blur-sm rounded-xl p-4 text-center">
                      <Gift className="w-8 h-8 mx-auto mb-2 text-green-400" />
                      <div className="text-cream font-semibold mb-1">Raport SEO</div>
                      <div className="text-beige-400 text-sm">Vlerë €150 - Falas!</div>
                    </div>
                    <div className="bg-bronze-800/50 backdrop-blur-sm rounded-xl p-4 text-center">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                      <div className="text-cream font-semibold mb-1">Konsultim 1h</div>
                      <div className="text-beige-400 text-sm">100% Falas</div>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      placeholder="Shkruaj email-in tënd..."
                      className="w-full px-6 py-4 bg-bronze-800 border-2 border-gold-400/30 rounded-xl text-cream placeholder:text-beige-400 focus:border-gold-400 focus:outline-none transition-colors disabled:opacity-50"
                    />
                    
                    {error && (
                      <p className="text-red-400 text-sm text-center">{error}</p>
                    )}
                    
                    <motion.button
                      whileHover={{ scale: isLoading ? 1 : 1.02 }}
                      whileTap={{ scale: isLoading ? 1 : 0.98 }}
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-gold text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-gold-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Duke dërguar...' : 'Merr Ofertën Tani 🎉'}
                      {!isLoading && <ArrowRight className="w-6 h-6" />}
                    </motion.button>
                  </form>

                  {/* Timer */}
                  <div className="mt-6 text-center">
                    <motion.p
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-red-400 font-semibold text-sm"
                    >
                      ⏰ Oferta skadon në 10 minuta!
                    </motion.p>
                  </div>

                  {/* Trust indicators */}
                  <div className="mt-6 flex items-center justify-center gap-6 text-beige-400 text-xs">
                    <span className="flex items-center gap-1">
                      ✓ Pa detyrim
                    </span>
                    <span className="flex items-center gap-1">
                      ✓ Anulo kur të duash
                    </span>
                    <span className="flex items-center gap-1">
                      ✓ 100% Falas
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring' }}
                    className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      ✓
                    </motion.div>
                  </motion.div>
                  
                  <h3 className="text-3xl font-bold text-cream mb-4">
                    🎉 Përgëzime!
                  </h3>
                  <p className="text-beige-300 text-lg mb-4">
                    Oferta u aktivizua me sukses!
                  </p>
                  <p className="text-beige-400 text-sm">
                    Kontrollo email-in për kodin e zbritjes dhe detaje të tjera.
                    Do të kontaktohemi brenda 24 orëve!
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ExitIntentPopup
